/**
 * Inflow icon library (line, monochrome, ~1.7 stroke).
 * 1:1 з design handoff'у (shared.jsx:Icon).
 * Список іконок з handoff'у README:
 *   crown, book, users, doc, play, user, chat, star, bot, shield,
 *   home, search, bell, arrow-up-right, arrow-right, flame, plus, menu.
 */

import { SOLAR_BODIES } from "@/components/iconBodies";

/**
 * ЄДИНІ ПРАВИЛА НАБОРУ (перенесено з досвіду Findly, ICONS_PLAYBOOK).
 *
 * 1. Іконки не малюємо руками — беремо Solar (один автор, одна пластика).
 *    Тіла лежать у `iconBodies.ts`, згенеровані `scripts/gen_icon_bodies.py`.
 * 2. Усі іконки живуть в ОДНІЙ сітці 15×15 з центром 12/12. Нормалізуємо
 *    ТРАНСФОРМАЦІЄЮ по реальному bbox, а не перемальовуванням шляхів.
 *    Перемалював шлях → перезамір bbox і новий рядок у `ICON_NORM`.
 * 3. Товщина лінії ОДНА на всю апку і задана в ПІКСЕЛЯХ ЕКРАНА (1.38 px).
 *    `strokeWidth` живе в координатах viewBox, тому те саме число дає різну
 *    лінію на різних розмірах — рахуємо від `size` і від масштабу.
 * 4. Тонше = дорожче. Не «вирівнювати» товщину догори і не робити шкалу
 *    за розміром: у Findly обидва варіанти власник забракував.
 */

/**
 * Центр viewBox — сюди зводимо центр рисунка кожної іконки.
 *
 * ⚠️ ІКОНКИ НЕ ПЕРЕМАСШТАБОВУЄМО. Спершу я тягнув кожну до спільної сітки
 * (15, потім 18) — і зламав саме те, за що набір беруть: Solar уже намальований
 * однією рукою, і різна ширина глифів у ньому НАВМИСНА (силует людини вужчий за
 * глобус — так його бачить око). Після мого масштабування іконки в стовпчику
 * стали різної ширини при однаковій висоті, і власник побачив «підвинуті влво,
 * ніхуя не центровані» (2026-09-10). Заміри при цьому давали ідеальний нуль:
 * геометрично вони СТОЯЛИ по центру — розсипалась саме оптика.
 *
 * Тому лишаємо рідний розмір глифа (рисунок Solar займає ~20 із 24 — це і є
 * «фарба ≈37% плашки» при наших кеглях) і тільки ЗВОДИМО ЦЕНТР: у частини
 * іконок центр bbox не збігається з 12/12, і ось це справді видно оком.
 */
/** Товщина лінії в пікселях ЕКРАНА — одна на всю апку. */
const STROKE_PX = 1.38;


/**
 * Лишилось як сумісність: у продуктових екранах не використовується. Розмір
 * і центрування визначає саме вікно viewBox (див. нижче), тому «режимів»
 * більше немає.
 */
export type IconFit = "height" | "width" | "native";
export const ICON_FIT_DEFAULT: IconFit = "native";

/**
 * ⛔️ ТУТ БУЛА ТАБЛИЦЯ ЦЕНТРУ МАСИ. Знято 11.09.2026: компенсація по масі
 * піднімала/опускала саму фігуру («іконка стрічки кудись угору пішла»).
 * Замість неї глифи-викиди замінені на 20×20-варіанти з того ж набору — тоді
 * центр маси й центр рамки практично збігаються самі.
 */

/**
 * Кегль іконки трактуємо як «дизайнерські px при базі 390» і віддаємо в rem —
 * так само, як `TgEmoji` (правило дизайн-системи: розміри в rem). Інакше
 * плашка масштабується від ширини екрана, а іконка лишається в px: на 430 px
 * вона відносно менша, і зазор у плашці перестає бути цілим — саме звідси
 * «іконки не всюди ідеально центровані» (owner 2026-09-10).
 */
const _rem = (n: number) => `${n / 16}rem`;

/**
 * Нормалізація: імʼя → [bboxW, bboxH, cx, cy]. Отримано заміром `getBBox()`
 * уже відрендерених іконок, а не розрахунком по шляху: у коді bbox не видно.
 * Ширина й висота лишились у таблиці для довідки (видно, які глифи вузькі),
 * але на рендер впливають ЛИШЕ cx/cy — зсув центру.
 */
const ICON_NORM: Record<string, [number, number, number, number]> = {
  // translate — іконка мов (owner 2026-09-20). bbox заміряно в браузері.
  "translate": [20, 20, 12, 12],
  // briefcase — «Вакансії чи замовлення» (owner 2026-09-20), замір у браузері.
  "briefcase": [20, 19, 12, 12.5],
  // Колесо фортуни (owner 2026-09-23): «Ще оберти». pie-chart стояла в
  // «Які у мене шанси» — рядок прибрано (owner 2026-09-30), іконка вільна.
  // Замір getBBox() у headless Chrome.
  "ticket": [20, 16, 12, 12],
  "pie-chart": [19.59, 19.59, 11.8, 12.2],
  "arrow-right": [16, 12, 12, 12],
  "arrow-up-right": [12, 12, 12, 12],
  "bell": [18, 20, 12, 12],
  "bold": [14, 20, 12, 12],
  "bolt": [16, 20, 12, 12],
  "book": [20, 18, 12, 12],
  "bookmark": [18, 20, 12, 12],
  "bookmark-fill": [18, 20, 12, 12],
  "bot": [20, 20, 12, 12],
  "brain": [20, 20, 12, 12],
  "cart": [18.161, 18, 11.08, 12],
  "chat": [20, 20, 12, 12],
  "check": [20, 20, 12, 12],
  "clear-format": [18, 18, 12, 12],
  "clock": [20, 20, 12, 12],
  "code": [18.021, 14.83, 12, 12.415],
  "crown": [20, 20, 12, 12],
  "doc": [18, 20, 12, 12],
  "edit": [16, 20, 12, 12],
  "filter": [20, 16, 12, 12],
  "flag": [20, 18, 12, 12],
  "flame": [17, 18.821, 11.5, 12.589],
  "flow": [20, 20, 12, 12],
  "gift": [20, 20, 12, 12],
  "globe": [20, 20, 12, 12],
  "headset": [20, 18, 12, 12],
  "help": [20, 20, 12, 12],
  "home": [20, 20, 12, 12],
  // inbox — «Без відповіді» в адмінці підтримки (owner 2026-09-25), замір
  // getBBox() у headless Chrome.
  "inbox": [20, 20, 12, 12],
  "italic": [18, 20, 12, 12],
  "key": [20, 20, 12, 12],
  "laptop": [20, 18, 12, 12],
  "link": [20, 18, 12, 12],
  "lock": [20, 20, 12, 12],
  "mail": [20, 16, 12, 12],
  "menu": [16, 10, 12, 12],
  "mic": [16, 19, 12, 12.5],
  "pause": [20, 20, 12, 12],
  "phone": [18, 18, 13, 11],
  "play": [20, 20, 12, 12],
  // project — контакт (owner 2026-09-24), замір getBBox у браузері.
  "project": [18, 20, 12, 12],
  // form — контакт (owner 2026-09-24), замір getBBox у браузері.
  "form": [20, 20, 12, 12],
  // chat-call — контакт (owner 2026-09-24), замір getBBox у браузері.
  "chat-call": [20, 20, 12, 12],
  // post — контакт (owner 2026-09-24), замір getBBox у браузері.
  "post": [20, 20, 12, 12],
  "plus": [20, 20, 12, 12],
  "renew": [20, 20, 12, 12],
  // «Крутити ще раз» у попапі виграшу колеса (owner 2026-10-26), solar:refresh-bold. Симетрична — центр bbox
  // і є центр рисунка. Замір getBBox у WebKit: 21.5×19.5, центр (12, 12).
  "respin": [21.5, 19.5, 12, 12],
  "rocket": [20, 20, 12, 12],
  "search": [20, 20, 12, 12],
  "settings": [18.856, 20, 12, 12],
  "shield": [18, 20, 12, 12],
  "snowflake": [18.589, 20, 12, 12],
  "star": [20, 20, 12, 12],
  "star-fill": [20, 20, 12, 12],
  "stop": [20, 20, 12, 12],
  "strike": [16, 18, 12, 12],
  "trash": [17, 18, 12, 12],
  "underline": [16, 18, 12, 12],
  "user": [14, 19, 12, 11.5],
  "users": [19, 19, 11.5, 11.5],
  "waveform": [16, 16, 12, 12],
  "whatsapp": [23.886, 24, 12, 12],
  "facebook": [24, 23.912, 12, 12],
  "linkedin": [24, 24, 12, 12],
  "chevron": [14, 6, 12, 12],
  // «Журнал дій» і «Зняти автопродовження» у вкладці «Чарджбеки» (owner
  // 2026-09-29). Замір getBBox() у headless Chrome.
  "history": [18, 18, 12, 12],
  "unlink": [18, 18, 12, 12],
  // «Акаунт підтримки» у вкладці «Підтримка» адмінки (owner 2026-10-01).
  // Замір getBBox() у headless Chrome.
  "account": [20, 16, 12, 12],
  // «Матеріали до уроку» під відео в базі знань (owner 2026-10-21).
  // Замір getBBox() у Chrome: x 2…22, y 2…22.
  "materials": [20, 20, 12, 12],
  // «MCP від inFLOW» і «Скопіювати» (owner 2026-10-30). Замір getBBox() у WebKit:
  // ai x 2…21, y 2…22; copy x 3…21, y 2…22.
  "ai": [19, 20, 11.5, 12],
  "copy": [18, 20, 12, 12],
  // Блок «Що вміє MCP від inFLOW» на сторінці MCP (owner 2026-11-01). Замір getBBox()
  // у Chrome і WebKit — однаковий.
  "card-search": [20, 16.5, 12, 12.25],
  "calendar": [20, 19.5, 12, 12.25],
  "clipboard-check": [18, 20, 12, 12],
  "pen-square": [20, 20, 12, 12],
  "folder-bookmark": [20, 20, 12, 12],
};

/** Іконки-заливки (Solar `-bold`): малюються fill, без обведення. */
const FILLED = new Set([
  "respin",
  "star-fill",
  "bookmark-fill",
  // офіційні марки брендів — заливка, не лінія
  "whatsapp",
  "facebook",
  "linkedin",
]);

export type IconName =
  | "translate"
  | "briefcase"
  | "ticket"
  | "pie-chart"
  | "crown"
  | "book"
  | "users"
  | "doc"
  | "play"
  | "user"
  | "chat"
  | "star"
  | "star-fill"
  | "bot"
  | "shield"
  | "search"
  | "bell"
  | "arrow-up-right"
  | "arrow-right"
  | "home"
  | "inbox"
  | "menu"
  | "plus"
  | "flame"
  | "bolt"
  | "flow"
  | "check"
  | "clock"
  | "brain"
  | "help"
  | "headset"
  | "rocket"
  | "threads"
  | "instagram"
  | "facebook"
  | "linkedin"
  | "whatsapp"
  | "globe"
  | "laptop"
  | "phone"
  | "post"
  | "project"
  | "form"
  | "chat-call"
  | "mail"
  | "telegram"
  | "mic"
  | "waveform"
  | "snowflake"
  | "stop"
  | "pause"
  | "renew"
  | "respin"
  | "ai"
  | "copy"
  | "cart"
  | "settings"
  | "lock"
  | "gift"
  | "trash"
  | "flag"
  | "key"
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "link"
  | "code"
  | "quote"
  | "clear-format"
  | "bookmark"
  | "bookmark-fill"
  | "edit"
  | "filter"
  | "chevron"
  | "history"
  | "unlink"
  | "account"
  | "materials"
  | "card-search"
  | "calendar"
  | "clipboard-check"
  | "pen-square"
  | "folder-bookmark";

interface IconProps {
  name: IconName;
  /** Лишилось для сумісності з галереєю; на рендер не впливає. */
  fit?: IconFit;
  size?: number;
  stroke?: number;
  color?: string;
  className?: string;
}

export function Icon({
  name,
  size = 22,
  stroke = 1.7,
  color,
  className,
}: IconProps) {
  const c = color ?? "currentColor";

  // ── Набір Solar: одна сітка, одна екранна товщина ─────────────────────────
  // Іконки з набору малюються ТУТ. Нижній `switch` лишився для брендів
  // (threads, instagram, facebook, linkedin, whatsapp, telegram) і для `quote`
  // — у Solar його немає. Решта кейсів у switch уже недосяжна: приберемо їх,
  // коли власник прийме новий набір (поки лежать, щоб можна було відкотитись
  // одним рядком).
  const body = SOLAR_BODIES[name];
  const norm = ICON_NORM[name];
  if (body && norm) {
    const [bw, bh, cx, cy] = norm;
    // ── Ключове рішення (11.09.2026) ─────────────────────────────────────────
    // Не крутимо рисунок трансформаціями і не «прилипаємо» до пікселів. Замість
    // цього робимо ВІКНО viewBox квадратним і центрованим на рисунку:
    //   viewBox = (cx − m/2, cy − m/2, m, m),  m = max(ширина, висота) bbox.
    // Тоді рисунок центрований у вікні ПО ВИЗНАЧЕННЮ, а вікно центрує сам
    // браузер — своєю точною математикою, без наших округлень. Саме тут
    // ламалось усе попереднє: власник бачив зсув на пів-пікселя, бо рисунок
    // сидів на дробових координатах.
    // `size` тепер = розмір самого РИСУНКА в px (а не порожньої рамки).
    // ⚠️ У вікно треба закласти ще й ОБВЕДЕННЯ: bbox — це геометрія шляху, а
    // лінія малюється по пів товщини з кожного боку. Без цього запасу зовнішні
    // штрихи обрізались, і від іконки лишались куточки (перша версія, 11.09).
    const filled = FILLED.has(name);
    const m0 = Math.max(bw, bh);
    // Запас на обведення потрібен ТІЛЬКИ лінійним: залита іконка не має штриха,
    // і з запасом вона малювалась на 8% дрібнішою за решту набору.
    const sw0 = filled ? 0 : (STROKE_PX * m0) / size;
    // ⚠️ ОБРІЗАННЯ ІКОНОК (owner 2026-09-12, тричі: «навіть хрестик і кошик
    // обрізані»). Вікно viewBox дорівнює межі фарби, а svg за замовчуванням
    // РІЖЕ все, що виходить за viewBox. Згладжений край лягав на саму межу й
    // зрізався на пів-пікселя — найпомітніше на гострих кутах.
    // Лікуємо не запасом (він робив би іконки дрібнішими), а `overflow:
    // visible` на самому svg: нічого не ріжеться, розміри лишаються ті, які
    // власник уже затвердив.
    const m = m0 + sw0;
    const sw = +((STROKE_PX * m) / size).toFixed(4);
    const vb = `${+(cx - m / 2).toFixed(3)} ${+(cy - m / 2).toFixed(3)} ${+m.toFixed(3)} ${+m.toFixed(3)}`;
    return (
      <svg
        width={_rem(size)}
        height={_rem(size)}
        viewBox={vb}
        fill="none"
        className={className}
        style={{ overflow: "visible" }}
      >
        <g
          {...(filled
            ? { fill: c }
            : {
                stroke: c,
                strokeWidth: sw,
                strokeLinecap: "round" as const,
                strokeLinejoin: "round" as const,
              })}
          dangerouslySetInnerHTML={{ __html: body }}
        />
      </svg>
    );
  }

  const common = {
    // rem, а не px: бренди мусять масштабуватись разом із набором Solar
    // (правило «розміри тільки в rem»; на 390 px різниці немає, на 360/430 —
    // бренди були на 9% меншими за решту).
    width: _rem(size),
    height: _rem(size),
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: c,
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className,
    style: { overflow: "visible" as const },
  };

  switch (name) {
    case "crown":
      return (
        <svg {...common}>
          <path d="M3 8l4 4 5-7 5 7 4-4-2 11H5L3 8z" />
          <path d="M5 19h14" />
        </svg>
      );
    case "book":
      // Feather-style book: симетрична книга з корінцем посередині.
      // Раніше був «open book з однією обкладинкою» — виглядав дивно.
      return (
        <svg {...common}>
          <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15z" />
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        </svg>
      );
    case "users":
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="3.2" />
          <circle cx="17" cy="10" r="2.4" />
          <path d="M3 19c.6-2.8 3-4.5 6-4.5s5.4 1.7 6 4.5" />
          <path d="M14.5 14.5c.8-.2 1.6-.3 2.5-.3 2.4 0 4.4 1.3 4.9 3.3" />
        </svg>
      );
    case "doc":
      return (
        <svg {...common}>
          <path d="M7 3h8l5 5v13H7V3z" />
          <path d="M15 3v5h5M10 13h7M10 17h5" />
        </svg>
      );
    case "play":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M10 8.5l6 3.5-6 3.5v-7z" fill={c} stroke="none" />
        </svg>
      );
    case "user":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M4 20c.8-3.6 4-5.5 8-5.5s7.2 1.9 8 5.5" />
        </svg>
      );
    case "chat":
      // Контент зміщено на -1 по Y: «хвостик» пузиря тягнув bbox донизу
      // (5..21, центр 13) → іконка сиділа нижче центру квадрата. Тепер
      // 4..20, центр рівно 12 (юзер 2026-06-14: «чатик не посередині»).
      return (
        <svg {...common}>
          <path d="M21 11.5C21 15.6 17 19 12 19c-1.3 0-2.6-.2-3.7-.6L3.5 20l1.6-3.8C4 15 3 13.3 3 11.5 3 7.4 7 4 12 4s9 3.4 9 7.5z" />
        </svg>
      );
    case "star":
      return (
        <svg {...common}>
          <path d="M12 3l2.7 5.7 6.3.9-4.6 4.4 1.1 6.3L12 17.4l-5.5 2.9 1.1-6.3L3 9.6l6.3-.9L12 3z" />
        </svg>
      );
    case "star-fill":
      return (
        <svg
          width={_rem(size)}
          height={_rem(size)}
          viewBox="0 0 24 24"
          fill={c}
          stroke={c}
          strokeWidth={stroke}
          strokeLinejoin="round"
          className={className}
          style={{ overflow: "visible" }}
        >
          <path d="M12 3l2.7 5.7 6.3.9-4.6 4.4 1.1 6.3L12 17.4l-5.5 2.9 1.1-6.3L3 9.6l6.3-.9L12 3z" />
        </svg>
      );
    case "clock":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "bot":
      // Контент зміщено на -1 по Y: корпус робота тепер центрований у
      // viewBox (6..18, центр 12) → іконка рівно по центру в кнопках/тайлах
      // (юзер 2026-06-14: «робот у кнопці не центрований»).
      return (
        <svg {...common}>
          <rect x="4" y="6" width="16" height="12" rx="3.5" />
          <circle cx="9" cy="12" r="1.3" fill={c} stroke="none" />
          <circle cx="15" cy="12" r="1.3" fill={c} stroke="none" />
          <path d="M12 3v3M9 18v2M15 18v2" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "search":
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="6.5" />
          <path d="M20 20l-4-4" />
        </svg>
      );
    case "bell":
      return (
        <svg {...common}>
          <path d="M6 16V11a6 6 0 1112 0v5l1.5 2H4.5L6 16z" />
          <path d="M10 21a2 2 0 004 0" />
        </svg>
      );
    case "arrow-up-right":
      return (
        <svg {...common}>
          <path d="M7 17L17 7M8 7h9v9" />
        </svg>
      );
    case "arrow-right":
      return (
        <svg {...common}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      );
    case "home":
      return (
        <svg {...common}>
          <path d="M4 11l8-7 8 7v9a1 1 0 01-1 1h-4v-6h-6v6H5a1 1 0 01-1-1v-9z" />
        </svg>
      );
    case "menu":
      return (
        <svg {...common}>
          <path d="M4 7h16M4 12h16M4 17h10" />
        </svg>
      );
    case "plus":
      return (
        <svg {...common}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "flame":
      // Розширений path: займає 15x20 з 24x24 viewBox (62%), як і
      // book/users/star у menu rows. Раніше було 10x15 (~42%).
      return (
        <svg {...common}>
          <path d="M12 1.5c1.4 5 7 6 7 12.5a7 7 0 11-14 0c0-2.6 1.3-4 2.6-5.2-.7 2.8.7 4.2 2 4.2 0-4 1.4-9.4 2.4-11.5z" />
        </svg>
      );
    case "bolt":
      // Молнія (owner 2026-09-10 — для картки «Відкрий повний доступ»: сенс
      // саме в миттєвості). Займає ~13x20 з 24x24, як flame/rocket, лінійна,
      // тієї ж товщини — без заливки, щоб не вибивалась із набору.
      return (
        <svg {...common}>
          <path d="M13.5 2.5L5.5 13.5h5l-1 8 8-11h-5l1-8z" />
        </svg>
      );
    case "flow":
      return (
        <svg {...common}>
          <path d="M3 17c4 0 4-10 9-10s5 10 9 10" />
        </svg>
      );
    case "check":
      return (
        <svg {...common}>
          <path d="M5 12l4 4L19 7" />
        </svg>
      );
    case "brain":
      // Симетричні дві півкулі + центральна лінія. Виглядає як мозок,
      // у нашому feather-style line-art.
      return (
        <svg {...common}>
          <path d="M9.5 3a3 3 0 0 0-3 3 3 3 0 0 0-2.5 3 3 3 0 0 0 .5 1.7A3 3 0 0 0 3 13a3 3 0 0 0 1.5 2.6A3 3 0 0 0 6.5 19a3 3 0 0 0 3 2h2.5V3h-2.5z" />
          <path d="M14.5 3a3 3 0 0 1 3 3 3 3 0 0 1 2.5 3 3 3 0 0 1-.5 1.7A3 3 0 0 1 21 13a3 3 0 0 1-1.5 2.6A3 3 0 0 1 17.5 19a3 3 0 0 1-3 2h-2.5V3h2.5z" />
          <path d="M12 3v18" />
        </svg>
      );
    case "help":
      // Знак запитання у крузі — feather-style (lucide HelpCircle).
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case "headset":
      // Навушники з мікрофоном (call-center) — lucide-style.
      return (
        <svg {...common}>
          <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
          <path d="M3 14h2.5a1.5 1.5 0 0 1 1.5 1.5V19a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 19v-5z" />
          <path d="M21 14h-2.5a1.5 1.5 0 0 0-1.5 1.5V19a1.5 1.5 0 0 0 1.5 1.5H19" />
          <path d="M21 14v5a4 4 0 0 1-4 4h-4" />
        </svg>
      );
    case "rocket":
      // Ракета з носом, корпусом, ілюмінатором, плавниками + полум'я.
      return (
        <svg {...common}>
          <path d="M12 2.5c-3 3-4.5 6-4.5 9.5v3l-1.5 2.5h12l-1.5-2.5v-3c0-3.5-1.5-6.5-4.5-9.5z" />
          <circle cx="12" cy="11" r="1.5" />
          <path d="M7.5 17.5l-2 4 3.5-1 M16.5 17.5l2 4-3.5-1 M11 21l1 2 1-2" />
        </svg>
      );
    case "threads":
      // Офіційний Threads logo (simpleicons.org — CC0). Заливка solid.
      return (
        <svg
          width={_rem(size)}
          height={_rem(size)}
          viewBox="0 0 24 24"
          fill={c}
          className={className}
          style={{ overflow: "visible" }}
        >
          <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z" />
        </svg>
      );
    case "instagram":
      // Офіційний Instagram logo (simpleicons.org — CC0). Заливка solid.
      return (
        <svg
          width={_rem(size)}
          height={_rem(size)}
          viewBox="0 0 24 24"
          fill={c}
          className={className}
          style={{ overflow: "visible" }}
        >
          <path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077" />
        </svg>
      );
    case "telegram":
      // Офіційний Telegram logo (simpleicons.org — CC0). Заливка solid.
      return (
        <svg
          width={_rem(size)}
          height={_rem(size)}
          viewBox="0 0 24 24"
          fill={c}
          className={className}
          style={{ overflow: "visible" }}
        >
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      );
    case "facebook":
      return (
        <svg {...common}>
          <path d="M14 9V7.5c0-.8.7-1.5 1.5-1.5H17V3h-2.5C12.6 3 11 4.6 11 6.5V9H8.5v3H11v9h3v-9h2.5l.5-3H14z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="M7 10v7 M7 7v.01 M11 17v-7 M11 13.5c0-2 4-2 4 0v3.5" />
        </svg>
      );
    case "whatsapp":
      return (
        <svg {...common}>
          <path d="M3 21l1.5-5A8.5 8.5 0 1 1 8 19.5L3 21z" />
          <path d="M8.5 10.5c0 3 2 5 5 5 .5 0 1-.5 1-1l-1.5-1c-.3-.2-.7-.1-.9.1-.4.4-.9.4-1.4-.1l-1.2-1.2c-.5-.5-.5-1 0-1.4.2-.2.3-.6.1-.9l-1-1.5c-.5 0-1 .5-1 1z" />
        </svg>
      );
    case "globe":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18 M12 3a13 13 0 0 1 0 18 M12 3a13 13 0 0 0 0 18" />
        </svg>
      );
    case "laptop":
      // Ноутбук — формат роботи (онлайн/офлайн/гібрид). Екран + база.
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="10" rx="1" />
          <path d="M2 19h20" />
        </svg>
      );
    case "phone":
      return (
        <svg {...common}>
          <path d="M5 4h4l1.5 4-2 1.5c1 2.5 3 4.5 5.5 5.5l1.5-2 4 1.5v4c0 .8-.7 1.5-1.5 1.5C9 20 4 15 4 5.5 4 4.7 4.7 4 5.5 4z" />
        </svg>
      );
    case "mail":
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case "mic":
      // Мікрофон (запис голосу) — капсула + дуга-підставка + ніжка.
      // Feather-style, для кнопки «Розповісти голосом».
      return (
        <svg {...common}>
          <rect x="9" y="2.5" width="6" height="11" rx="3" />
          <path d="M6 11a6 6 0 0 0 12 0" />
          <path d="M12 17v4M9 21h6" />
        </svg>
      );
    case "waveform":
      // Звукова хвиля голосового — вертикальні бари різної висоти,
      // як запис voice-message (юзер 2026-06-14: «не мікрофон, а
      // іконка по типу запис голосового»). Симетрично навколо центру.
      return (
        <svg {...common} strokeWidth={2.1}>
          <path d="M3 10.5v3M6.5 7.5v9M10 4.5v15M13.5 8.5v7M17 6v12M20.5 10v4" />
        </svg>
      );
    case "snowflake":
      // Сніжинка (заморозка) — 6 променів через центр (60° між осями)
      // + V-гілочки на кінцях. Lucide-style, симетрична.
      return (
        <svg {...common}>
          <path d="M12 2v20" />
          <path d="M4.2 7l15.6 10" />
          <path d="M19.8 7L4.2 17" />
          <path d="M12 2l-2 2.2M12 2l2 2.2M12 22l-2-2.2M12 22l2-2.2" />
          <path d="M4.2 7l.5 2.9M4.2 7l2.7-.9M19.8 17l-.5-2.9M19.8 17l-2.7.9" />
          <path d="M19.8 7l-2.7-.9M19.8 7l-.5 2.9M4.2 17l2.7.9M4.2 17l.5-2.9" />
        </svg>
      );
    case "stop":
      // Стоп (пауза підписки) — заокруглений квадрат, ідеально центрований
      // у 24×24 viewBox (центр 12,12). Юзер 2026-06-14: заморозку показуємо
      // іконкою «стоп», не льодинкою.
      return (
        <svg {...common}>
          <rect x="5.5" y="5.5" width="13" height="13" rx="3.5" />
        </svg>
      );
    case "pause":
      // Пауза В КОЛІ (заморозка/призупинення підписки). Юзер 2026-06-14:
      // самі смужки не читались як іконка → пауза в кружечку. Коло r9.5 +
      // дві смужки всередині, все центровано (центр 12,12).
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9.5" />
          <path d="M9.8 8.8v6.4M14.2 8.8v6.4" strokeWidth={2.2} />
        </svg>
      );
    case "renew":
      // Колові стрілки (refresh-cw, lucide) — продовжити/оновити підписку.
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.36 2.64L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.36-2.64L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      );
    case "bold":
      // Панель форматування розсилки (owner 2026-08-27). Літери малюємо тим
      // самим лінійним стилем, що й решта бібліотеки — без емодзі і без
      // сторонніх наборів.
      return (
        <svg {...common}>
          <path d="M7.6 4.4h5a3.5 3.5 0 0 1 0 7h-5z" />
          <path d="M7.6 11.4h5.8a4 4 0 0 1 0 8H7.6z" />
        </svg>
      );
    case "italic":
      return (
        <svg {...common}>
          <path d="M15.4 4.6H9.8" />
          <path d="M14.2 19.4H8.6" />
          <path d="M13.6 4.6 10.4 19.4" />
        </svg>
      );
    case "underline":
      return (
        <svg {...common}>
          <path d="M6.8 3.8v6.6a5.2 5.2 0 0 0 10.4 0V3.8" />
          <path d="M5.6 20.2h12.8" />
        </svg>
      );
    case "strike":
      return (
        <svg {...common}>
          <path d="M3.8 12h16.4" />
          <path d="M16.6 7.4c-.6-1.8-2.2-2.9-4.4-2.9-2.7 0-4.5 1.4-4.5 3.4 0 1.4.9 2.4 2.6 3.1" />
          <path d="M7.8 16.4c.7 1.8 2.4 3 4.7 3 2.8 0 4.6-1.4 4.6-3.4 0-1-.4-1.9-1.2-2.5" />
        </svg>
      );
    case "link":
      return (
        <svg {...common}>
          <path d="M10.2 13.8a3.6 3.6 0 0 0 5.4.4l2.6-2.6a3.6 3.6 0 0 0-5.1-5.1l-1.5 1.5" />
          <path d="M13.8 10.2a3.6 3.6 0 0 0-5.4-.4l-2.6 2.6a3.6 3.6 0 0 0 5.1 5.1l1.5-1.5" />
        </svg>
      );
    case "code":
      return (
        <svg {...common}>
          <path d="M9 7.2 4.2 12 9 16.8" />
          <path d="M15 7.2 19.8 12 15 16.8" />
        </svg>
      );
    case "quote":
      return (
        <svg {...common}>
          <path d="M4.6 5.4v13.2" />
          <path d="M9.4 8.4h10" />
          <path d="M9.4 12h10" />
          <path d="M9.4 15.6h6.4" />
        </svg>
      );
    case "clear-format":
      return (
        <svg {...common}>
          <path d="M7.4 6.4V4.8h11.2v1.6" />
          <path d="M13 4.8 10.2 19.2" />
          <path d="M8 19.2h5.2" />
          <path d="m16.2 14.4 4.4 4.8" />
          <path d="m20.6 14.4-4.4 4.8" />
        </svg>
      );
    case "trash":
      // Кошик для сміття — «прибрати вакансію в себе». Той самий лінійний
      // стиль, що й решта бібліотеки (owner 2026-08-16: емодзі 🗑 випадав із
      // дизайну — будь-яка нова кнопка бере іконку звідси, а не з емодзі).
      return (
        <svg {...common}>
          <path d="M3.4 5.9h17.2" />
          <path d="M9 5.9V3.6a1.4 1.4 0 0 1 1.4-1.4h3.2a1.4 1.4 0 0 1 1.4 1.4v2.3" />
          <path d="M5.6 5.9l1 14.1a1.7 1.7 0 0 0 1.7 1.6h7.4a1.7 1.7 0 0 0 1.7-1.6l1-14.1" />
          <path d="M10 10.2v7" />
          <path d="M14 10.2v7" />
        </svg>
      );
    case "flag":
      // Прапорець скарги «Це не вакансія» — раніше був інлайновим svg у двох
      // місцях, тепер один раз тут.
      return (
        <svg {...common}>
          <path d="M4.8 14.4s1-.9 3.6-.9 4.5 1.8 7.2 1.8 3.6-.9 3.6-.9V3.6s-.9.9-3.6.9-4.5-1.8-7.2-1.8-3.6.9-3.6.9z" />
          <path d="M4.8 21.6v-7.2" />
        </svg>
      );
    case "cart":
      // Кошик покупок (купити заморозку/підписку) — feather-style.
      return (
        <svg {...common}>
          <circle cx="9" cy="20" r="1.4" fill={c} stroke="none" />
          <circle cx="18" cy="20" r="1.4" fill={c} stroke="none" />
          <path d="M2.5 3h2.2l2 11.5a1.5 1.5 0 0 0 1.5 1.2h8.5a1.5 1.5 0 0 0 1.5-1.2L21 7H6" />
        </svg>
      );
    case "settings":
      // Шестерня (lucide settings) — справжні зубці, не «сонце».
      // Для розділу «Автопродовження / Керування».
      return (
        <svg {...common}>
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "filter":
      // Лійка (чорний список слів, owner 2026-09-06) — лінійна, 24×24.
      return (
        <svg {...common}>
          <path d="M4 5h16l-6.2 7.4v5.3l-3.6 2.1v-7.4L4 5Z" />
        </svg>
      );
    case "edit":
      // Олівець (змінити дату народження, owner 2026-09-07) — той самий
      // лінійний стиль: 24×24, stroke 1.7.
      return (
        <svg {...common}>
          <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
          <path d="M13.5 6.5l4 4" />
        </svg>
      );
    case "bookmark":
      // Закладка (зберегти вакансію, owner 2026-09-06) — той самий лінійний
      // стиль, що й решта: 24×24, stroke 1.7, без заливки.
      return (
        <svg {...common}>
          <path d="M6.5 4.5h11a1 1 0 0 1 1 1v14l-6.5-4-6.5 4v-14a1 1 0 0 1 1-1Z" />
        </svg>
      );
    case "bookmark-fill":
      // Та сама закладка, але залита — стан «збережено».
      return (
        <svg {...common} fill="currentColor">
          <path d="M6.5 4.5h11a1 1 0 0 1 1 1v14l-6.5-4-6.5 4v-14a1 1 0 0 1 1-1Z" />
        </svg>
      );
    case "lock":
      // Замок (доступно з підпискою) — feather-style.
      return (
        <svg {...common}>
          <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
          <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
          <path d="M12 14.5v2.5" />
        </svg>
      );
    case "key":
      // Ключ (відкрити контакти) — lucide «key-round»: гладке кільце-борідка
      // зверху-справа, стрижень із зубцями донизу-зліва + отвір. Чистий,
      // преміальний (юзер 2026-06-14: попередній був «дешевий»).
      return (
        <svg {...common}>
          <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 1 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
          <circle cx="16.5" cy="7.5" r="0.6" fill={c} stroke="none" />
        </svg>
      );
    case "gift":
      // Подарунок (lucide gift) — коробка + бант + вертикальна стрічка.
      // Центрований (3..21 по обох осях → центр 12).
      return (
        <svg {...common}>
          <rect x="3.5" y="8" width="17" height="4" rx="1" />
          <path d="M12 8v13" />
          <path d="M19 12v7.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5V12" />
          <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
        </svg>
      );
  }
}

// ── Sketchy SVG accents (з kit-elements.svg) ────────────────────────

export function ScribbleUnderline({
  width = 220,
  color = "#FE6B04",
  className = "",
  stretch = false,
}: {
  width?: number;
  color?: string;
  className?: string;
  /** true → розтягуємо по ширині БЕЗ зміни товщини (preserveAspectRatio
   * none). За замовчуванням false — старе uniform-масштабування, щоб не
   * чіпати решту сторінок. */
  stretch?: boolean;
}) {
  return (
    <svg
      width={width}
      height="14"
      viewBox="0 0 220 14"
      fill="none"
      className={className}
      preserveAspectRatio={stretch ? "none" : undefined}
    >
      <path
        d="M2 9 C 30 2, 70 12, 110 6 C 150 1, 180 11, 218 5"
        stroke={color}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ScribbleStar({
  size = 28,
  color = "#FE6B04",
  className = "",
}: {
  size?: number;
  color?: string;
  className?: string;
}) {
  return (
    <svg
      width={_rem(size)}
      height={_rem(size)}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      style={{ overflow: "visible" }}
    >
      <path
        d="M14 3 C 16 11, 18 12, 25 14 C 17 16, 16 17, 14 25 C 12 17, 11 16, 3 14 C 11 12, 12 11, 14 3z"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}
